// ═══════════════════════════════════════════════════════════════════════════════
// VideoScreen.js — Jitsi-powered video interview, external browser mode
//
// Why external browser:
//   React Native WebView (especially in Expo Go) can't reliably grant
//   camera/mic permissions to in-page getUserMedia calls. Opening Jitsi
//   in Safari/Chrome bypasses this entirely — it just works.
//
// Flow:
//   1. Pre-join: show interview details + shareable link
//   2. Tap "Start Interview" → Jitsi launches in the device browser
//   3. App stays on a Control Panel screen with all the recruiter tools
//      (Notes, Questions, Evaluation, Invite, End). Notes/questions/eval
//      live in the app so they're saved to the DB, not trapped in Jitsi.
//   4. Tap End → persists interview metadata to applications table
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Modal, TextInput, Alert, Dimensions,
  Platform, StatusBar, BackHandler, ScrollView, Image, Share,
  KeyboardAvoidingView, Linking,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';

let Clipboard;
try { Clipboard = require('expo-clipboard'); }
catch { Clipboard = { setStringAsync: async () => false }; }

const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY      = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const apiFetch = async (endpoint, options = {}) => {
  try {
    const token = await AsyncStorage.getItem('auth_token').catch(() => null);
    const res = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
      body: options.body && typeof options.body === 'object' ? JSON.stringify(options.body) : options.body,
    });
    if (!res.ok) return { error: true, status: res.status };
    return await res.json();
  } catch (err) {
    return { error: true, message: err.message };
  }
};

const T = {
  bg:     '#0b0f1a',
  panel:  '#111827',
  card:   '#1f2937',
  border: '#374151',
  text:   '#f9fafb',
  sub:    '#9ca3af',
  mute:   '#6b7280',
  primary:'#0e4e96',
  accent: '#10b981',
  warn:   '#f59e0b',
  danger: '#ef4444',
};

const fmtDuration = (s) => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
};

const buildRoomName = (interviewId, applicantName) => {
  const slug = String(applicantName || 'interview')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 30) || 'candidate';
  const id = String(interviewId || `i${Date.now()}`).replace(/[^a-zA-Z0-9]/g, '');
  return `JobEng-${slug}-${id}`;
};

const VideoScreen = ({ navigation, route }) => {
  const params = route?.params || {};
  const interviewId       = params.interviewId    || `interview_${Date.now()}`;
  const applicationId     = params.applicationId  || params.applicantId || params.application_id || null;
  const applicantName     = params.applicantName  || params.candidate_name || 'Candidate';
  const applicantEmail    = params.applicantEmail || params.candidate_email || '';
  const applicantPhone    = params.applicantPhone || '';
  const applicantPosition = params.position       || params.jobTitle || '';
  const applicantDept     = params.department     || '';
  const interviewType     = params.interviewType  || 'technical';
  const agenda            = params.agenda         || '';

  const [me, setMe]                   = useState({ name: 'Recruiter', email: '' });
  const [companyLogo, setCompanyLogo] = useState(params.companyLogo || null);
  const [companyName, setCompanyName] = useState(params.companyName || '');

  const [stage, setStage]                 = useState('prejoin');
  const [duration, setDuration]           = useState(0);
  const [startedAt, setStartedAt]         = useState(null);
  const [meetingOpenedAt, setMeetingOpenedAt] = useState(null);

  const [activePanel, setActivePanel]     = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEndModal, setShowEndModal]     = useState(false);

  const [notes, setNotes]                 = useState('');
  const [questions, setQuestions]         = useState([
    { id: 1, q: 'Walk me through your most recent project.', asked: false },
    { id: 2, q: 'Tell me about a time you disagreed with a teammate.', asked: false },
    { id: 3, q: 'What is your approach to debugging production issues?', asked: false },
  ]);
  const [newQuestion, setNewQuestion] = useState('');
  const [evaluation, setEvaluation]   = useState({
    technical: 0, communication: 0, culture: 0, overall: 0, recommend: '', summary: '',
  });

  const durationTimer   = useRef(null);
  const saveDraftTimer  = useRef(null);
  const meetingEndedRef = useRef(false);

  const roomName     = useMemo(() => buildRoomName(interviewId, applicantName), [interviewId, applicantName]);
  const meetingUrl   = `https://meet.jit.si/${roomName}`;
  const recruiterUrl = `${meetingUrl}#userInfo.displayName=%22${encodeURIComponent(me.name || 'Recruiter')}%22&config.prejoinPageEnabled=false`;
  const candidateUrl = `${meetingUrl}#userInfo.displayName=%22${encodeURIComponent(applicantName)}%22`;

  useFocusEffect(useCallback(() => {
    (async () => {
      try {
        const cached = await AsyncStorage.getItem('company_profile');
        if (cached) {
          const p = JSON.parse(cached);
          setCompanyLogo(prev => prev || p.logo || null);
          setCompanyName(prev => prev || p.name || p.company_name || '');
        }
        const userRaw = await AsyncStorage.getItem('userData') || await AsyncStorage.getItem('user');
        if (userRaw) {
          const u = JSON.parse(userRaw);
          const user = u.user || u;
          setMe({ name: user.full_name || user.name || 'Recruiter', email: user.email || '' });
        }
      } catch {}
    })();
  }, []));

  useEffect(() => {
    (async () => {
      try {
        const raw = await AsyncStorage.getItem(`interview_draft_${interviewId}`);
        if (raw) {
          const d = JSON.parse(raw);
          if (d.notes)      setNotes(d.notes);
          if (d.questions)  setQuestions(d.questions);
          if (d.evaluation) setEvaluation(d.evaluation);
        }
      } catch {}
    })();
  }, [interviewId]);

  useEffect(() => {
    if (saveDraftTimer.current) clearTimeout(saveDraftTimer.current);
    saveDraftTimer.current = setTimeout(() => {
      AsyncStorage.setItem(
        `interview_draft_${interviewId}`,
        JSON.stringify({ notes, questions, evaluation })
      ).catch(() => {});
    }, 800);
    return () => saveDraftTimer.current && clearTimeout(saveDraftTimer.current);
  }, [notes, questions, evaluation, interviewId]);

  useEffect(() => {
    if (stage === 'live' && !durationTimer.current) {
      durationTimer.current = setInterval(() => setDuration(d => d + 1), 1000);
    }
    if (stage !== 'live' && durationTimer.current) {
      clearInterval(durationTimer.current);
      durationTimer.current = null;
    }
    return () => {
      if (durationTimer.current) { clearInterval(durationTimer.current); durationTimer.current = null; }
    };
  }, [stage]);

  useEffect(() => {
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      if (stage === 'live' && !meetingEndedRef.current) {
        setShowEndModal(true);
        return true;
      }
      return false;
    });
    return () => sub.remove();
  }, [stage]);

  useEffect(() => {
    const unsub = navigation.addListener('beforeRemove', (e) => {
      if (stage === 'live' && !meetingEndedRef.current) {
        e.preventDefault();
        setShowEndModal(true);
      }
    });
    return unsub;
  }, [navigation, stage]);

  const openVideoInBrowser = async () => {
    try {
      await Linking.openURL(recruiterUrl);
      setMeetingOpenedAt(new Date().toISOString());
    } catch (e) {
      Alert.alert('Could not open', 'No browser is available. Try copying the link manually.');
    }
  };

  const startInterview = () => {
    setStartedAt(new Date().toISOString());
    setStage('live');
    openVideoInBrowser();
  };

  const copyLink = async (url) => {
    try {
      await Clipboard.setStringAsync(url);
      Alert.alert('Copied', 'Link copied to clipboard');
    } catch {
      Alert.alert('Copy', url);
    }
  };

  const shareLink = async () => {
    const body =
      `You're invited to an interview${applicantPosition ? ` for ${applicantPosition}` : ''}${companyName ? ` at ${companyName}` : ''}.\n\n` +
      `Join the video call: ${candidateUrl}\n\n` +
      (agenda ? `Agenda:\n${agenda}\n\n` : '') +
      `No app or account needed — the link opens in any modern browser.`;
    try {
      await Share.share({ title: `Interview: ${applicantName}`, message: body });
    } catch {}
  };

  const emailCandidate = () => {
    if (!applicantEmail) {
      Alert.alert('No email', 'This candidate has no email on file.');
      return;
    }
    const subject = encodeURIComponent(`Interview link${applicantPosition ? ` — ${applicantPosition}` : ''}`);
    const body = encodeURIComponent(
      `Hi ${applicantName.split(' ')[0] || 'there'},\n\n` +
      `Please join your interview${companyName ? ` with ${companyName}` : ''} using this link:\n` +
      `${candidateUrl}\n\n` +
      (agenda ? `Agenda:\n${agenda}\n\n` : '') +
      `It opens in any modern browser — no app needed.\n\n` +
      `Best,\n${me.name}`
    );
    Linking.openURL(`mailto:${applicantEmail}?subject=${subject}&body=${body}`).catch(() => {});
  };

  const endMeeting = async (opts = {}) => {
    meetingEndedRef.current = true;
    setShowEndModal(false);
    const endedAt = new Date().toISOString();
    setStage('ended');

    if (applicationId) {
      const update = {
        interview_room_id:     roomName,
        interview_url:         meetingUrl,
        interview_started_at:  startedAt,
        interview_ended_at:    endedAt,
        interview_duration_s:  duration,
        interview_type:        interviewType,
        interview_notes:       notes || null,
        interview_evaluation:  evaluation,
        interview_questions:   questions,
        stage: opts.newStage || undefined,
      };
      Object.keys(update).forEach(k => update[k] === undefined && delete update[k]);
      await apiFetch(`/api/data/applications/${applicationId}`, { method: 'PUT', body: update }).catch(() => {});
      await apiFetch('/api/data/application_status_history', {
        method: 'POST',
        body: {
          application_id: applicationId,
          stage:  opts.newStage || 'interview',
          status: 'interview_completed',
          note: `Interview conducted · ${fmtDuration(duration)}${evaluation.recommend ? ` · Recommendation: ${evaluation.recommend}` : ''}`,
          created_at: endedAt,
        },
      }).catch(() => {});
    }
    AsyncStorage.removeItem(`interview_draft_${interviewId}`).catch(() => {});

    Alert.alert(
      'Interview ended',
      `Duration: ${fmtDuration(duration)}\n\nNotes & evaluation have been saved.`,
      [{ text: 'OK', onPress: () => navigation.goBack() }]
    );
  };

  const toggleAsked = (id) => setQuestions(qs => qs.map(q => q.id === id ? { ...q, asked: !q.asked } : q));
  const addQuestion = () => {
    if (!newQuestion.trim()) return;
    setQuestions(qs => [...qs, { id: Date.now(), q: newQuestion.trim(), asked: false }]);
    setNewQuestion('');
  };

  // ═══ PRE-JOIN ═══
  if (stage === 'prejoin') {
    return (
      <SafeAreaView style={st.safe} edges={['top','bottom']}>
        <StatusBar barStyle="light-content" backgroundColor={T.bg} />
        <LinearGradient colors={[T.primary, '#0a3a72']} style={st.prejoinHeader}>
          <TouchableOpacity style={st.iconBtn} onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={22} color="#fff" />
          </TouchableOpacity>
          <Text style={st.prejoinHeaderTitle}>Interview room</Text>
          <View style={{ width: 40 }} />
        </LinearGradient>

        <ScrollView contentContainerStyle={{ padding: 20 }}>
          {companyLogo ? (
            <Image source={{ uri: companyLogo }} style={st.companyLogo} />
          ) : (
            <View style={[st.companyLogo, { backgroundColor: T.card, alignItems: 'center', justifyContent: 'center' }]}>
              <MaterialCommunityIcons name="domain" size={40} color={T.sub} />
            </View>
          )}
          {companyName ? <Text style={st.companyName}>{companyName}</Text> : null}

          <Text style={st.prejoinApplicantName}>{applicantName}</Text>
          {applicantPosition ? <Text style={st.prejoinRole}>{applicantPosition}</Text> : null}
          {applicantDept ? <Text style={st.prejoinDept}>{applicantDept}</Text> : null}

          <View style={st.prejoinMetaRow}>
            <View style={st.prejoinChip}>
              <MaterialCommunityIcons name="video-outline" size={14} color={T.sub} />
              <Text style={st.prejoinChipText}>{interviewType}</Text>
            </View>
            <View style={st.prejoinChip}>
              <MaterialCommunityIcons name="shield-check-outline" size={14} color={T.accent} />
              <Text style={[st.prejoinChipText, { color: T.accent }]}>Secure · Jitsi</Text>
            </View>
          </View>

          {agenda ? (
            <View style={st.prejoinCard}>
              <Text style={st.prejoinCardTitle}>Agenda</Text>
              <Text style={st.prejoinCardBody}>{agenda}</Text>
            </View>
          ) : null}

          <View style={st.prejoinCard}>
            <Text style={st.prejoinCardTitle}>How it works</Text>
            <HowRow n={1}>Tap <Text style={{ fontWeight: '700', color: T.text }}>Start Interview</Text> — the video call opens in your browser</HowRow>
            <HowRow n={2}>Allow camera + microphone in the browser prompt</HowRow>
            <HowRow n={3}>Switch back to this app anytime for notes, questions, evaluation</HowRow>
            <HowRow n={4}>Tap <Text style={{ fontWeight: '700', color: T.text }}>End</Text> when done — everything saves automatically</HowRow>
          </View>

          <View style={st.prejoinCard}>
            <Text style={st.prejoinCardTitle}>Candidate link</Text>
            <Text style={[st.prejoinCardBody, { color: T.sub, fontSize: 12, marginTop: 4 }]} numberOfLines={1}>
              {candidateUrl}
            </Text>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <TouchableOpacity style={[st.smallBtn, { backgroundColor: T.card, borderColor: T.border, borderWidth: 1, flex: 1 }]} onPress={() => copyLink(candidateUrl)}>
                <MaterialCommunityIcons name="content-copy" size={14} color={T.text} />
                <Text style={st.smallBtnText}>Copy</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[st.smallBtn, { backgroundColor: T.card, borderColor: T.border, borderWidth: 1, flex: 1 }]} onPress={shareLink}>
                <MaterialCommunityIcons name="share-variant" size={14} color={T.text} />
                <Text style={st.smallBtnText}>Share</Text>
              </TouchableOpacity>
              {applicantEmail ? (
                <TouchableOpacity style={[st.smallBtn, { backgroundColor: T.primary, flex: 1 }]} onPress={emailCandidate}>
                  <MaterialCommunityIcons name="email-outline" size={14} color="#fff" />
                  <Text style={[st.smallBtnText, { color: '#fff' }]}>Email</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          </View>

          <View style={{ height: 20 }} />
          <TouchableOpacity style={st.joinBtn} onPress={startInterview}>
            <MaterialCommunityIcons name="video" size={20} color="#fff" />
            <Text style={st.joinBtnText}>Start Interview</Text>
          </TouchableOpacity>
          <Text style={st.prejoinHint}>
            The video call opens in Safari/Chrome. Your notes and evaluation stay here in the app.
          </Text>
        </ScrollView>
      </SafeAreaView>
    );
  }

  // ═══ LIVE CONTROL PANEL ═══
  const totalQuestions = questions.length;
  const askedQuestions = questions.filter(q => q.asked).length;
  const evalFilled     = [evaluation.technical, evaluation.communication, evaluation.culture, evaluation.overall].filter(v => v > 0).length;

  return (
    <SafeAreaView style={st.safe} edges={['top','bottom']}>
      <StatusBar barStyle="light-content" backgroundColor={T.bg} />

      <View style={st.liveHeaderBar}>
        <View style={st.liveDot} />
        <View style={{ flex: 1 }}>
          <Text style={st.liveHeaderTitle} numberOfLines={1}>{applicantName}</Text>
          {applicantPosition ? <Text style={st.liveHeaderSub} numberOfLines={1}>{applicantPosition}</Text> : null}
        </View>
        <View style={st.durationPill}>
          <MaterialCommunityIcons name="clock-outline" size={12} color="#fff" />
          <Text style={st.durationText}>{fmtDuration(duration)}</Text>
        </View>
      </View>

      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 120 }}>
        <TouchableOpacity style={st.videoCard} onPress={openVideoInBrowser}>
          <LinearGradient colors={[T.accent, '#059669']} style={st.videoCardGrad}>
            <View style={st.videoCardIcon}>
              <MaterialCommunityIcons name="video" size={32} color="#fff" />
            </View>
            <View style={{ flex: 1, marginLeft: 14 }}>
              <Text style={st.videoCardTitle}>{meetingOpenedAt ? 'Rejoin video call' : 'Open video call'}</Text>
              <Text style={st.videoCardSub}>{meetingOpenedAt ? 'Returns to your browser' : 'Opens in Safari / Chrome'}</Text>
            </View>
            <Ionicons name="open-outline" size={22} color="#fff" />
          </LinearGradient>
        </TouchableOpacity>

        <View style={st.gridRow}>
          <ToolCard icon="help-circle-outline" label="Questions" count={`${askedQuestions}/${totalQuestions}`} active={askedQuestions > 0} onPress={() => setActivePanel('questions')} />
          <ToolCard icon="note-text-outline"   label="Notes"     count={notes ? `${notes.split(/\s+/).filter(Boolean).length}w` : '—'} active={notes.length > 0} onPress={() => setActivePanel('notes')} />
        </View>
        <View style={st.gridRow}>
          <ToolCard icon="clipboard-check-outline" label="Evaluation" count={`${evalFilled}/4`} active={evalFilled > 0} onPress={() => setActivePanel('evaluation')} />
          <ToolCard icon="share-variant"           label="Invite"     count="Share"              active={false}         onPress={() => setShowShareModal(true)} />
        </View>

        <View style={st.quickNotesCard}>
          <View style={st.quickNotesHeader}>
            <MaterialCommunityIcons name="note-text-outline" size={16} color={T.accent} />
            <Text style={st.quickNotesTitle}>Quick notes</Text>
          </View>
          <TextInput
            style={st.quickNotesInput}
            multiline
            placeholder="Jot down thoughts as the interview happens…"
            placeholderTextColor={T.mute}
            value={notes}
            onChangeText={setNotes}
          />
          <Text style={st.panelHint}>Auto-saved · tap Notes above for fullscreen</Text>
        </View>

        <View style={st.infoCard}>
          <Text style={st.infoCardTitle}>Candidate</Text>
          <Row label="Name"  value={applicantName} />
          <Row label="Role"  value={applicantPosition} />
          <Row label="Email" value={applicantEmail} copy />
          <Row label="Phone" value={applicantPhone} copy />
        </View>
      </ScrollView>

      <View style={st.bottomBar}>
        <TouchableOpacity style={[st.endBtn, { backgroundColor: T.danger }]} onPress={() => setShowEndModal(true)}>
          <MaterialCommunityIcons name="phone-hangup" size={18} color="#fff" />
          <Text style={st.endBtnText}>End interview</Text>
        </TouchableOpacity>
      </View>

      <SlidePanel visible={activePanel === 'notes'} onClose={() => setActivePanel(null)} title="Interview notes">
        <TextInput
          style={st.notesArea}
          multiline
          placeholder="Observations, strengths, concerns, follow-ups…"
          placeholderTextColor={T.mute}
          value={notes}
          onChangeText={setNotes}
        />
        <Text style={st.panelHint}>Notes save automatically as you type.</Text>
      </SlidePanel>

      <SlidePanel visible={activePanel === 'questions'} onClose={() => setActivePanel(null)} title="Questions">
        <ScrollView>
          {questions.map(q => (
            <TouchableOpacity key={q.id} style={st.questionRow} onPress={() => toggleAsked(q.id)}>
              <View style={[st.checkbox, q.asked && st.checkboxActive]}>
                {q.asked && <Ionicons name="checkmark" size={14} color="#fff" />}
              </View>
              <Text style={[st.questionText, q.asked && { color: T.sub, textDecorationLine: 'line-through' }]}>
                {q.q}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        <View style={st.addQuestionRow}>
          <TextInput
            style={st.addQuestionInput}
            placeholder="Add a question…"
            placeholderTextColor={T.mute}
            value={newQuestion}
            onChangeText={setNewQuestion}
            onSubmitEditing={addQuestion}
          />
          <TouchableOpacity style={st.addBtn} onPress={addQuestion}>
            <Ionicons name="add" size={22} color="#fff" />
          </TouchableOpacity>
        </View>
      </SlidePanel>

      <SlidePanel visible={activePanel === 'evaluation'} onClose={() => setActivePanel(null)} title="Evaluation">
        <ScrollView>
          <RatingRow label="Technical skills" value={evaluation.technical}     onChange={v => setEvaluation(e => ({ ...e, technical: v }))} />
          <RatingRow label="Communication"    value={evaluation.communication} onChange={v => setEvaluation(e => ({ ...e, communication: v }))} />
          <RatingRow label="Culture fit"      value={evaluation.culture}       onChange={v => setEvaluation(e => ({ ...e, culture: v }))} />
          <RatingRow label="Overall"          value={evaluation.overall}       onChange={v => setEvaluation(e => ({ ...e, overall: v }))} />

          <Text style={st.panelLabel}>Recommendation</Text>
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
            {[
              { k: 'hire',    label: 'Hire',    color: T.accent  },
              { k: 'maybe',   label: 'Maybe',   color: T.warn    },
              { k: 'no_hire', label: 'No hire', color: T.danger  },
            ].map(o => (
              <TouchableOpacity
                key={o.k}
                style={[st.recBtn, evaluation.recommend === o.k && { backgroundColor: o.color, borderColor: o.color }]}
                onPress={() => setEvaluation(e => ({ ...e, recommend: o.k }))}
              >
                <Text style={[st.recBtnText, evaluation.recommend === o.k && { color: '#fff' }]}>{o.label}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={st.panelLabel}>Summary</Text>
          <TextInput
            style={[st.notesArea, { marginTop: 8, minHeight: 100 }]}
            multiline
            placeholder="One-paragraph summary for the team…"
            placeholderTextColor={T.mute}
            value={evaluation.summary}
            onChangeText={v => setEvaluation(e => ({ ...e, summary: v }))}
          />
        </ScrollView>
      </SlidePanel>

      <Modal visible={showShareModal} transparent animationType="slide" onRequestClose={() => setShowShareModal(false)}>
        <TouchableOpacity style={st.modalBg} activeOpacity={1} onPress={() => setShowShareModal(false)}>
          <View style={st.modalCard}>
            <Text style={st.modalTitle}>Invite the candidate</Text>
            <Text style={st.modalBody} numberOfLines={2}>{candidateUrl}</Text>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 14 }}>
              <TouchableOpacity style={[st.smallBtn, { flex: 1, backgroundColor: T.card, borderWidth: 1, borderColor: T.border }]} onPress={() => copyLink(candidateUrl)}>
                <MaterialCommunityIcons name="content-copy" size={16} color={T.text} />
                <Text style={st.smallBtnText}>Copy link</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[st.smallBtn, { flex: 1, backgroundColor: T.primary }]} onPress={shareLink}>
                <MaterialCommunityIcons name="share-variant" size={16} color="#fff" />
                <Text style={[st.smallBtnText, { color: '#fff' }]}>Share</Text>
              </TouchableOpacity>
            </View>
            {applicantEmail ? (
              <TouchableOpacity style={[st.smallBtn, { backgroundColor: T.accent, marginTop: 8 }]} onPress={emailCandidate}>
                <MaterialCommunityIcons name="email-outline" size={16} color="#fff" />
                <Text style={[st.smallBtnText, { color: '#fff' }]}>Email to {applicantEmail}</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        </TouchableOpacity>
      </Modal>

      <Modal visible={showEndModal} transparent animationType="fade" onRequestClose={() => setShowEndModal(false)}>
        <View style={st.modalBg}>
          <View style={st.modalCard}>
            <MaterialCommunityIcons name="phone-hangup" size={36} color={T.danger} style={{ alignSelf: 'center' }} />
            <Text style={[st.modalTitle, { textAlign: 'center', marginTop: 8 }]}>End interview?</Text>
            <Text style={[st.modalBody, { textAlign: 'center' }]}>
              Duration: {fmtDuration(duration)}.{'\n'}Notes and evaluation will be saved.
            </Text>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 16 }}>
              <TouchableOpacity style={[st.smallBtn, { flex: 1, backgroundColor: T.card, borderWidth: 1, borderColor: T.border }]} onPress={() => setShowEndModal(false)}>
                <Text style={st.smallBtnText}>Continue</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[st.smallBtn, { flex: 1, backgroundColor: T.danger }]} onPress={() => endMeeting()}>
                <MaterialCommunityIcons name="phone-hangup" size={16} color="#fff" />
                <Text style={[st.smallBtnText, { color: '#fff' }]}>End now</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
};

const HowRow = ({ n, children }) => (
  <View style={st.howRow}>
    <View style={st.howBullet}><Text style={st.howNum}>{n}</Text></View>
    <Text style={st.howText}>{children}</Text>
  </View>
);

const ToolCard = ({ icon, label, count, active, onPress }) => (
  <TouchableOpacity style={[st.toolCard, active && st.toolCardActive]} onPress={onPress}>
    <MaterialCommunityIcons name={icon} size={22} color={active ? T.accent : T.text} />
    <Text style={st.toolCardLabel}>{label}</Text>
    <Text style={[st.toolCardCount, active && { color: T.accent }]}>{count}</Text>
  </TouchableOpacity>
);

const Row = ({ label, value, copy }) => {
  const handleCopy = async () => {
    if (!value) return;
    try {
      await Clipboard.setStringAsync(String(value));
      Alert.alert('Copied', value);
    } catch {}
  };
  if (!value) return null;
  return (
    <View style={st.infoRow}>
      <Text style={st.infoLabel}>{label}</Text>
      <Text style={st.infoValue} numberOfLines={1}>{value}</Text>
      {copy && (
        <TouchableOpacity onPress={handleCopy} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
          <MaterialCommunityIcons name="content-copy" size={14} color={T.sub} />
        </TouchableOpacity>
      )}
    </View>
  );
};

const SlidePanel = ({ visible, onClose, title, children }) => (
  <Modal visible={visible} animationType="slide" transparent onRequestClose={onClose}>
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      style={{ flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.6)' }}
    >
      <TouchableOpacity style={{ flex: 1 }} activeOpacity={1} onPress={onClose} />
      <View style={st.slidePanel}>
        <View style={st.slidePanelHeader}>
          <Text style={st.slidePanelTitle}>{title}</Text>
          <TouchableOpacity onPress={onClose} style={st.iconBtn}>
            <Ionicons name="close" size={22} color={T.text} />
          </TouchableOpacity>
        </View>
        <View style={{ flex: 1 }}>{children}</View>
      </View>
    </KeyboardAvoidingView>
  </Modal>
);

const RatingRow = ({ label, value, onChange }) => (
  <View style={st.ratingRow}>
    <Text style={st.panelLabel}>{label}</Text>
    <View style={{ flexDirection: 'row', gap: 6 }}>
      {[1,2,3,4,5].map(n => (
        <TouchableOpacity key={n} onPress={() => onChange(n === value ? 0 : n)}>
          <MaterialCommunityIcons name={n <= value ? 'star' : 'star-outline'} size={24} color={n <= value ? T.warn : T.mute} />
        </TouchableOpacity>
      ))}
    </View>
  </View>
);

const st = StyleSheet.create({
  safe: { flex: 1, backgroundColor: T.bg },
  iconBtn: { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },

  prejoinHeader: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 12 },
  prejoinHeaderTitle: { flex: 1, color: '#fff', fontSize: 17, fontWeight: '700', textAlign: 'center' },
  companyLogo: { width: 80, height: 80, borderRadius: 12, alignSelf: 'center', marginTop: 10 },
  companyName: { color: T.sub, fontSize: 13, textAlign: 'center', marginTop: 8 },
  prejoinApplicantName: { color: T.text, fontSize: 24, fontWeight: '800', textAlign: 'center', marginTop: 20 },
  prejoinRole: { color: T.sub, fontSize: 15, textAlign: 'center', marginTop: 6 },
  prejoinDept: { color: T.mute, fontSize: 13, textAlign: 'center', marginTop: 2 },

  prejoinMetaRow: { flexDirection: 'row', justifyContent: 'center', gap: 8, marginTop: 14 },
  prejoinChip: { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: T.card, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 14 },
  prejoinChipText: { color: T.sub, fontSize: 12, textTransform: 'capitalize' },

  prejoinCard: { backgroundColor: T.panel, borderRadius: 14, padding: 14, marginTop: 16, borderWidth: 1, borderColor: T.border },
  prejoinCardTitle: { color: T.sub, fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5 },
  prejoinCardBody: { color: T.text, fontSize: 14, marginTop: 6, lineHeight: 20 },

  howRow: { flexDirection: 'row', alignItems: 'flex-start', marginTop: 12 },
  howBullet: { width: 22, height: 22, borderRadius: 11, backgroundColor: T.primary, alignItems: 'center', justifyContent: 'center', marginRight: 10, marginTop: 1 },
  howNum: { color: '#fff', fontSize: 12, fontWeight: '800' },
  howText: { flex: 1, color: T.sub, fontSize: 13, lineHeight: 19 },

  joinBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: T.accent, paddingVertical: 16, borderRadius: 14 },
  joinBtnText: { color: '#fff', fontSize: 16, fontWeight: '800' },
  prejoinHint: { color: T.mute, fontSize: 12, textAlign: 'center', marginTop: 10, paddingHorizontal: 10 },

  smallBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 10, paddingHorizontal: 12, borderRadius: 10 },
  smallBtnText: { color: T.text, fontSize: 13, fontWeight: '600' },

  liveHeaderBar: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, backgroundColor: T.panel, borderBottomWidth: 1, borderBottomColor: T.border, gap: 10 },
  liveDot: { width: 10, height: 10, borderRadius: 5, backgroundColor: T.accent },
  liveHeaderTitle: { color: T.text, fontSize: 15, fontWeight: '700' },
  liveHeaderSub: { color: T.sub, fontSize: 12, marginTop: 2 },
  durationPill: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: T.card, paddingVertical: 6, paddingHorizontal: 12, borderRadius: 14 },
  durationText: { color: '#fff', fontSize: 12, fontWeight: '700' },

  videoCard: { borderRadius: 14, overflow: 'hidden', marginBottom: 16 },
  videoCardGrad: { flexDirection: 'row', alignItems: 'center', padding: 16 },
  videoCardIcon: { width: 50, height: 50, borderRadius: 25, backgroundColor: 'rgba(255,255,255,0.2)', alignItems: 'center', justifyContent: 'center' },
  videoCardTitle: { color: '#fff', fontSize: 17, fontWeight: '800' },
  videoCardSub: { color: 'rgba(255,255,255,0.85)', fontSize: 12, marginTop: 2 },

  gridRow: { flexDirection: 'row', gap: 10, marginBottom: 10 },
  toolCard: { flex: 1, backgroundColor: T.panel, borderRadius: 14, padding: 14, borderWidth: 1, borderColor: T.border, alignItems: 'flex-start', gap: 6 },
  toolCardActive: { borderColor: T.accent, backgroundColor: 'rgba(16,185,129,0.08)' },
  toolCardLabel: { color: T.text, fontSize: 13, fontWeight: '700' },
  toolCardCount: { color: T.sub, fontSize: 11 },

  quickNotesCard: { backgroundColor: T.panel, borderRadius: 14, padding: 14, borderWidth: 1, borderColor: T.border, marginTop: 16 },
  quickNotesHeader: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  quickNotesTitle: { color: T.text, fontSize: 13, fontWeight: '700' },
  quickNotesInput: { backgroundColor: T.card, color: T.text, borderRadius: 10, padding: 10, marginTop: 10, minHeight: 80, textAlignVertical: 'top', fontSize: 13 },

  infoCard: { backgroundColor: T.panel, borderRadius: 14, padding: 14, borderWidth: 1, borderColor: T.border, marginTop: 16 },
  infoCardTitle: { color: T.sub, fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
  infoRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, gap: 8 },
  infoLabel: { width: 70, color: T.sub, fontSize: 12 },
  infoValue: { flex: 1, color: T.text, fontSize: 13, fontWeight: '500' },

  bottomBar: { position: 'absolute', left: 0, right: 0, bottom: 0, flexDirection: 'row', padding: 12, paddingBottom: Platform.OS === 'ios' ? 30 : 12, backgroundColor: T.panel, borderTopWidth: 1, borderTopColor: T.border },
  endBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, paddingVertical: 14, borderRadius: 12 },
  endBtnText: { color: '#fff', fontSize: 15, fontWeight: '800' },

  slidePanel: { backgroundColor: T.panel, borderTopLeftRadius: 20, borderTopRightRadius: 20, paddingHorizontal: 16, paddingBottom: Platform.OS === 'ios' ? 34 : 18, maxHeight: '75%' },
  slidePanelHeader: { flexDirection: 'row', alignItems: 'center', paddingTop: 14, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: T.border, marginBottom: 14 },
  slidePanelTitle: { flex: 1, color: T.text, fontSize: 16, fontWeight: '700' },
  panelLabel: { color: T.sub, fontSize: 12, fontWeight: '600', marginTop: 12, textTransform: 'uppercase', letterSpacing: 0.3 },
  panelHint: { color: T.mute, fontSize: 11, marginTop: 8 },

  notesArea: { backgroundColor: T.card, color: T.text, borderRadius: 10, padding: 12, minHeight: 180, textAlignVertical: 'top', borderWidth: 1, borderColor: T.border, fontSize: 14 },

  questionRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10 },
  checkbox: { width: 22, height: 22, borderRadius: 6, borderWidth: 1.5, borderColor: T.sub, alignItems: 'center', justifyContent: 'center', marginRight: 10 },
  checkboxActive: { backgroundColor: T.accent, borderColor: T.accent },
  questionText: { flex: 1, color: T.text, fontSize: 14, lineHeight: 20 },

  addQuestionRow: { flexDirection: 'row', gap: 8, marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: T.border },
  addQuestionInput: { flex: 1, backgroundColor: T.card, color: T.text, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, borderWidth: 1, borderColor: T.border },
  addBtn: { width: 44, height: 44, borderRadius: 22, backgroundColor: T.primary, alignItems: 'center', justifyContent: 'center' },

  ratingRow: { marginTop: 10 },
  recBtn: { flex: 1, paddingVertical: 10, borderRadius: 10, backgroundColor: T.card, borderWidth: 1, borderColor: T.border, alignItems: 'center' },
  recBtnText: { color: T.text, fontSize: 13, fontWeight: '700' },

  modalBg: { flex: 1, backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'flex-end' },
  modalCard: { backgroundColor: T.panel, borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, paddingBottom: Platform.OS === 'ios' ? 34 : 20 },
  modalTitle: { color: T.text, fontSize: 17, fontWeight: '700' },
  modalBody: { color: T.sub, fontSize: 13, marginTop: 8, lineHeight: 19 },
});

export default VideoScreen;
